'use client';

import type { MysteryRoomHeroState, MysteryRoomLocalRuntime, MysteryRoomNpcState } from '../core/localRuntime';
import type { MysteryRoomSegment, MysteryRoomVec2 } from '../world/mapTypes';
import { buildSightBlockingSegments, hasUnobstructedSightLine } from './visibilitySystem';
import { normalizeInputVector } from './inputSystem';
import { facingFromVelocity } from './movementSystem';
import { npcHasSecurityBehavior, npcUsesZombieBehavior, resolveNpcFaction } from './npcBehaviorTreeSystem';

const NPC_SEARCH_SCAN_MS = 1400;
const NPC_SUSPICION_ALERT_MS = 600;
const MOVING_HERO_SUSPICION_MULTIPLIER = 2;

export function updateNpcDetection(runtime: MysteryRoomLocalRuntime, dtMs: number) {
  const blockingSegments = buildSightBlockingSegments(runtime.map, runtime.doors);
  const nextNpcs = runtime.npcs.map((npc) => {
    if (!npc.alive) {
      return {
        ...npc,
        seesHero: false,
        suspicionMs: 0,
        awarenessState: 'patrol' as const,
        investigateTarget: null,
        searchRemainingMs: 0,
      };
    }

    const seesHero =
      runtime.hero.hidden ? false : npcCanInvestigateHero(runtime, npc) && npcCanSeeHero(npc, runtime.hero, blockingSegments);

    return {
      ...npc,
      ...resolveNpcAwareness(npc, runtime.hero, seesHero, dtMs),
    };
  });
  const spottedByNpcIds = nextNpcs
    .filter((npc) => npc.seesHero && npc.awarenessState === 'alert')
    .map((npc) => npc.id);
  const hasSuspiciousNpc = nextNpcs.some(
    (npc) => npc.awarenessState === 'suspicious' || npc.awarenessState === 'search' || npc.suspicionMs > 0,
  );
  const nextHero = {
    ...runtime.hero,
    alertState: spottedByNpcIds.length > 0 ? ('spotted' as const) : hasSuspiciousNpc ? ('suspicious' as const) : ('clear' as const),
    spottedByNpcIds,
  };

  return {
    hero: nextHero,
    npcs: nextNpcs,
    events: buildDetectionEvents(runtime, nextHero, nextNpcs),
  };
}

function npcCanInvestigateHero(runtime: MysteryRoomLocalRuntime, npc: MysteryRoomNpcState) {
  if (npcUsesZombieBehavior(npc)) {
    return false;
  }

  const faction = resolveNpcFaction(npc);
  if (faction === 'hostile') {
    return true;
  }

  if (faction !== 'staff') {
    return false;
  }

  if (!npcHasSecurityBehavior(npc)) {
    return false;
  }

  if (npc.awarenessState === 'alert' || npc.awarenessState === 'search' || npc.awarenessState === 'suspicious') {
    return true;
  }

  if (runtime.worldAlarm.state === 'active') {
    return true;
  }

  const heroRoom = runtime.map.rooms.find((room) => room.id === runtime.hero.zoneId);
  if (heroRoom && !runtime.hero.accessLevels.includes(heroRoom.accessLevel)) {
    return true;
  }

  return runtime.incidents.some(
    (incident) => incident.status !== 'resolved' && incident.sourceType === 'player' && incident.sourceActorId === 'hero',
  );
}

function resolveNpcAwareness(
  npc: MysteryRoomNpcState,
  hero: MysteryRoomHeroState,
  seesHero: boolean,
  dtMs: number,
) {
  if (seesHero) {
    if (npc.awarenessState === 'alert' || npc.awarenessState === 'search') {
      return {
        seesHero: true,
        suspicionMs: NPC_SUSPICION_ALERT_MS,
        awarenessState: 'alert' as const,
        investigateTarget: { ...hero.position },
        searchRemainingMs: NPC_SEARCH_SCAN_MS,
      };
    }

    const suspicionMs = Math.min(
      NPC_SUSPICION_ALERT_MS,
      npc.suspicionMs + suspicionGainMs(hero, dtMs),
    );
    if (suspicionMs < NPC_SUSPICION_ALERT_MS) {
      return {
        seesHero: true,
        suspicionMs,
        awarenessState: 'suspicious' as const,
        investigateTarget: { ...hero.position },
        searchRemainingMs: 0,
      };
    }

    return {
      seesHero: true,
      suspicionMs,
      awarenessState: 'alert' as const,
      investigateTarget: { ...hero.position },
      searchRemainingMs: NPC_SEARCH_SCAN_MS,
    };
  }

  if ((npc.awarenessState === 'alert' || npc.awarenessState === 'search') && npc.investigateTarget) {
    return {
      seesHero: false,
      suspicionMs: 0,
      awarenessState: 'search' as const,
      investigateTarget: { ...npc.investigateTarget },
      searchRemainingMs: npc.searchRemainingMs,
    };
  }

  const suspicionMs = Math.max(0, npc.suspicionMs - dtMs);
  if (suspicionMs > 0) {
    return {
      seesHero: false,
      suspicionMs,
      awarenessState: 'suspicious' as const,
      investigateTarget: npc.investigateTarget ? { ...npc.investigateTarget } : null,
      searchRemainingMs: 0,
    };
  }

  return {
    seesHero: false,
    suspicionMs: 0,
    awarenessState: 'patrol' as const,
    investigateTarget: null,
    searchRemainingMs: 0,
  };
}

function suspicionGainMs(hero: MysteryRoomHeroState, dtMs: number) {
  const heroIsMoving = hero.movementMode !== 'idle' || Math.hypot(hero.velocity.x, hero.velocity.y) > 0.001;
  return heroIsMoving ? dtMs * MOVING_HERO_SUSPICION_MULTIPLIER : dtMs;
}

function buildDetectionEvents(
  runtime: MysteryRoomLocalRuntime,
  hero: MysteryRoomHeroState,
  npcs: MysteryRoomNpcState[],
) {
  const previousSpotters = new Set(runtime.hero.spottedByNpcIds);
  const newlySpotted = hero.spottedByNpcIds.filter((npcId) => !previousSpotters.has(npcId));

  if (newlySpotted.length > 0) {
    const labels = newlySpotted
      .map((npcId) => npcs.find((npc) => npc.id === npcId)?.name ?? npcId)
      .join(', ');

    return [`Тревога: ${labels} заметил игрока`, ...runtime.events].slice(0, 10);
  }

  if (runtime.hero.alertState === 'clear' && hero.alertState === 'suspicious') {
    return ['Охрана насторожилась', ...runtime.events].slice(0, 10);
  }

  if (runtime.hero.alertState === 'spotted' && hero.alertState !== 'spotted') {
    return ['Охрана потеряла визуальный контакт', ...runtime.events].slice(0, 10);
  }

  return runtime.events;
}

function npcCanSeeHero(
  npc: MysteryRoomNpcState,
  hero: MysteryRoomHeroState,
  blockingSegments: MysteryRoomSegment[],
) {
  const delta = {
    x: hero.position.x - npc.position.x,
    y: hero.position.y - npc.position.y,
  };
  const distance = Math.hypot(delta.x, delta.y);

  if (distance > npc.visionRange) {
    return false;
  }

  const visionFacing =
    npc.awarenessState === 'suspicious' && npc.investigateTarget
      ? facingFromVelocity(
          {
            x: npc.investigateTarget.x - npc.position.x,
            y: npc.investigateTarget.y - npc.position.y,
          },
          npc.facing,
        )
      : npc.facing;

  if (!pointInsideViewCone(npc.position, visionFacing, npc.visionHalfAngleDeg, hero.position)) {
    return false;
  }

  return hasUnobstructedSightLine(npc.position, hero.position, blockingSegments);
}

function pointInsideViewCone(
  origin: MysteryRoomVec2,
  facing: MysteryRoomNpcState['facing'],
  halfAngleDeg: number,
  target: MysteryRoomVec2,
) {
  const facingVector = normalizeInputVector(facingToVector(facing));
  const toTarget = normalizeInputVector({
    x: target.x - origin.x,
    y: target.y - origin.y,
  });
  const dot = facingVector.x * toTarget.x + facingVector.y * toTarget.y;

  return dot >= Math.cos((halfAngleDeg * Math.PI) / 180);
}

function facingToVector(facing: MysteryRoomNpcState['facing']) {
  switch (facing) {
    case 'up':
      return { x: 0, y: -1 };
    case 'down':
      return { x: 0, y: 1 };
    case 'left':
      return { x: -1, y: 0 };
    case 'right':
      return { x: 1, y: 0 };
  }
}
